import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Loader2 } from 'lucide-react';
import PropTypes from 'prop-types';

import API from '@/apiRequest';
import copyIcon from '@/components/common/icons/copy.svg';
import renderSvg from '@/utils/renderSvg';
import { EASYSIGN_BASE_URL } from '@/config';
import { showToast } from '@/store/ducks/toasts';
import ClientIcon from '@/svg-icons/client';
import DeclinedIcon from '@/svg-icons/declined';
import AcceptedIcon from '@/svg-icons/status-done';

const BrySignersSection = ({ orderId, refreshHandler, signers, requestId, copyToClipboard }) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [loadingAll, setLoadingAll] = useState(false);
  const [loadingSignerIds, setLoadingSignerIds] = useState(() => new Set());

  const addSignerLoading = useCallback(signerId => {
    setLoadingSignerIds(prev => {
      const next = new Set(prev);
      next.add(signerId);
      return next;
    });
  }, []);

  const removeSignerLoading = useCallback(signerId => {
    setLoadingSignerIds(prev => {
      const next = new Set(prev);
      next.delete(signerId);
      return next;
    });
  }, []);

  const renderStatusIcon = useCallback(status => {
    const normalizedStatus = (status || '').toLowerCase();

    if (normalizedStatus === 'signed' || normalizedStatus === 'finished')
      return <AcceptedIcon viewBox="0 0 55 55" className="w-10 h-10 mb-2 text-green-500 shrink-0" />;
    if (normalizedStatus === 'refused' || normalizedStatus === 'declined')
      return <DeclinedIcon viewBox="0 0 55 55" className="w-10 h-10 mb-2 text-green-500 shrink-0" />;

    return null;
  }, []);

  const handleNotifySigner = useCallback(
    async (signerId, signerNonce) => {
      if (!signerId || !signerNonce) return;

      addSignerLoading(signerId);
      try {
        await API.put(`/orders/${orderId}`, {
          type: 'marketplace_bry',
          action: 'notify_signer',
          signer_nonce: signerNonce,
        });

        dispatch(showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'toast.success.title', defaultMessage: 'Sucesso!' }),
          text: intl.formatMessage({ id: 'orderDetails.bry.notifySignerSuccess', defaultMessage: 'Notificação reenviada.' }),
          duration: 5000,
        }));

        if (typeof refreshHandler === 'function') refreshHandler();
      } catch (error) {
        dispatch(showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'toast.error.title', defaultMessage: 'Erro!' }),
          text: intl.formatMessage({ id: 'orderDetails.bry.notifySignerError', defaultMessage: 'Não foi possível reenviar a notificação.' }),
          duration: 5000,
        }));
      } finally {
        removeSignerLoading(signerId);
      }
    },
    [addSignerLoading, dispatch, intl, orderId, refreshHandler, removeSignerLoading]
  );

  const handleNotifyAll = useCallback(async () => {
    setLoadingAll(true);
    try {
      await API.put(`/orders/${orderId}`, {
        type: 'marketplace_bry',
        action: 'notify_signature',
      });

      dispatch(showToast({
        type: 'success',
        title: intl.formatMessage({ id: 'toast.success.title', defaultMessage: 'Sucesso!' }),
        text: intl.formatMessage({ id: 'orderDetails.bry.notifyAllSuccess', defaultMessage: 'Notificações enviadas para todos os signatários.' }),
        duration: 5000,
      }));

      if (typeof refreshHandler === 'function') refreshHandler();
    } catch (error) {
      dispatch(showToast({
        type: 'error',
        title: intl.formatMessage({ id: 'toast.error.title', defaultMessage: 'Erro!' }),
        text: intl.formatMessage({ id: 'orderDetails.bry.notifyAllError', defaultMessage: 'Não foi possível enviar as notificações.' }),
        duration: 5000,
      }));
    } finally {
      setLoadingAll(false);
    }
  }, [dispatch, intl, orderId, refreshHandler]);

  const hasSigners = signers.length > 0;

  const allSigned = useMemo(() => {
    return signers.every(signer => {
      const normalizedStatus = (signer.status || '').toLowerCase();
      return normalizedStatus === 'signed' || normalizedStatus === 'finished';
    });
  }, [signers]);

  const renderSignerActions = useCallback(
    signer => {
      if (!signer?.id) return null;

      const normalizedStatus = (signer.status || '').toLowerCase();
      const isSigned = normalizedStatus === 'signed' || normalizedStatus === 'finished';
      const isLoading = signer.id ? loadingSignerIds.has(signer.id) : false;

      if (isSigned) return null;

      const handleCopyLink = async () => {
        if (!requestId || !signer.nonce) return;
        const url = `${EASYSIGN_BASE_URL}/${requestId}/sign/${signer.nonce}`;
        copyToClipboard(url);
      };

      return (
        <div className="flex flex-col gap-[0.5vw]">
          <button
            className={`flex items-center justify-center gap-[0.5vw] px-[1vw] py-[0.4vw] rounded shadow-sm text-[calc(5px+0.4vw)] uppercase font-bold transition-all disabled:opacity-50 ${
              isLoading ? 'bg-[#D7DFAE] text-[#5B5B5B] cursor-not-allowed' : 'bg-[#00BBDD] text-white hover:bg-[#0099B3]'
            }`}
            disabled={isLoading}
            onClick={() => handleNotifySigner(signer.id, signer.nonce)}
          >
            {isLoading ? (
              <Loader2 className="w-[1vw] h-[1vw] animate-spin text-white" />
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="w-[1vw] h-[1vw]" viewBox="0 0 16 16" fill="none">
                  <path d="M8.89444 0.552786C8.72505 0.214002 8.37878 0 8.00001 0C7.62124 0 7.27498 0.214002 7.10558 0.552786L0.105584 14.5528C-0.0717665 14.9075 -0.0227602 15.3335 0.230502 15.6386C0.483764 15.9438 0.893423 16.0705 1.27473 15.9615L6.27472 14.533C6.70402 14.4103 7 14.0179 7 13.5714V9C7 8.44771 7.44772 8 8 8C8.55229 8 9 8.44771 9 9V13.5714C9 14.0179 9.29598 14.4103 9.72528 14.533L14.7253 15.9615C15.1066 16.0705 15.5163 15.9438 15.7695 15.6386C16.0228 15.3335 16.0718 14.9075 15.8944 14.5528L8.89444 0.552786Z" fill="white" />
                </svg>
                <span>{intl.formatMessage({ id: 'orderDetails.bry.notifySigner', defaultMessage: 'Reenviar notificação' })}</span>
              </>
            )}
          </button>

          <button
            className="flex items-center justify-center gap-[0.5vw] px-[1vw] py-[0.4vw] rounded border border-[#D9E0E7] bg-white text-[#5B6670] text-[calc(5px+0.4vw)] font-bold uppercase transition-all hover:bg-[#F5F7FA]"
            onClick={handleCopyLink}
            title={intl.formatMessage({ id: 'orderDetails.bry.copySignatureTooltip', defaultMessage: 'Copiar link de assinatura' })}
          >
            {renderSvg(copyIcon, { className: 'w-[1vw] h-[1vw]', alt: 'Copiar' })}
            <span>{intl.formatMessage({ id: 'orderDetails.bry.copySignatureTooltip', defaultMessage: 'Copiar link' })}</span>
          </button>
        </div>
      );
    },
    [handleNotifySigner, intl, loadingSignerIds, requestId, copyToClipboard]
  );

  if (!hasSigners) return null;

  return (
    <div className="w-full">
      <div className="font-bold text-[calc(6px+0.4vw)] my-[0.6vw] flex items-center">
        <ClientIcon 
          viewBox="0 0 34.016 34.016" 
          className="text-gray-700 mr-2 w-6 h-6 shrink-0" 
        />
        <FormattedMessage id="signers" />
      </div>

      <div className="flex flex-col text-[#858585] text-[calc(6px+0.4vw)] my-[1vw] pl-[calc(14px+1.8vw)]">
        {signers.map((signer, index) => {
          const signerKey = signer.id || signer.email || index;
          const signerAction = renderSignerActions(signer);

          return (
            <div key={signerKey} className="mb-[1.5vw] last:mb-0" data-cy="order-details-bry-signer">
              <p className="font-bold border-b border-[#F0F0F0] pb-[0.3vw] mb-[0.8vw]">
                <FormattedMessage id="signers" /> <span className="text-[calc(5px+0.4vw)]">{index + 1}</span>
              </p>
              <div className="flex justify-between items-center gap-[1vw]">
                <div className="grow">
                  <p className="font-bold m-0">{signer.name}</p>
                  <p className="m-0 opacity-80">{signer.email}</p>
                </div>
                <div className="flex items-center gap-[1vw]">
                  {signerAction && <div>{signerAction}</div>}
                  {renderStatusIcon(signer.status)}
                </div>
              </div>
            </div>
          );
        })}

        {hasSigners && !allSigned && (
          <div className="flex justify-end mt-[1vw]">
            <button
              className={`flex items-center justify-center gap-[0.6vw] px-[1.5vw] py-[0.5vw] rounded shadow-sm text-[calc(5px+0.4vw)] uppercase font-bold transition-all disabled:opacity-50 ${
                loadingAll ? 'bg-[#D7DFAE] text-[#5B5B5B] cursor-not-allowed' : 'bg-[#00BBDD] text-white hover:bg-[#0099B3]'
              }`}
              disabled={loadingAll}
              onClick={handleNotifyAll}
            >
              {loadingAll ? (
                <Loader2 className="w-[1vw] h-[1vw] animate-spin text-white" />
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-[1vw] h-[1vw]" viewBox="0 0 16 16" fill="none">
                    <path d="M8.89444 0.552786C8.72505 0.214002 8.37878 0 8.00001 0C7.62124 0 7.27498 0.214002 7.10558 0.552786L0.105584 14.5528C-0.0717665 14.9075 -0.0227602 15.3335 0.230502 15.6386C0.483764 15.9438 0.893423 16.0705 1.27473 15.9615L6.27472 14.533C6.70402 14.4103 7 14.0179 7 13.5714V9C7 8.44771 7.44772 8 8 8C8.55229 8 9 8.44771 9 9V13.5714C9 14.0179 9.29598 14.4103 9.72528 14.533L14.7253 15.9615C15.1066 16.0705 15.5163 15.9438 15.7695 15.6386C16.0228 15.3335 16.0718 14.9075 15.8944 14.5528L8.89444 0.552786Z" fill="white" />
                  </svg>
                  <span>{intl.formatMessage({ id: 'orderDetails.bry.notifyAll', defaultMessage: 'Notificar todos' })}</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

BrySignersSection.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  refreshHandler: PropTypes.func,
  signers: PropTypes.arrayOf(
    PropTypes.shape({
      email: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      status: PropTypes.string,
    })
  ).isRequired,
  requestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  copyToClipboard: PropTypes.func,
};

BrySignersSection.defaultProps = {
  refreshHandler: undefined,
  requestId: undefined,
};

export default BrySignersSection;
